import React, { useEffect, useState } from 'react';
import { TemplateResult } from './SurveyTemplates';
import { useTestTemplate } from './SurveyTemplates/TemplateEditor/TemplateEditorModal/useTestTemplate';
import SurveyResponse from '../util/SurveyResponse';
import { useSurveyTemplates } from '../util/useSurveyTemplates';
import { SurveyTemplateWithId } from '../../../services/SurveyService';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { CheckCircle, XCircle } from '@lifeomic/chromicons';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { defineMessages, useIntl } from 'react-intl';
import { permissions, enforce, Policy } from '../../../util/abac';
import { useCustomCompareEffect } from 'react-use';
import _isEqual from 'lodash/isEqual';

type QuestionnaireResponse = fhir.QuestionnaireResponse;

type Props = {
  response: SurveyResponse;
  surveyId: string;
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    height: '100%',
    position: 'sticky',
    top: 0,
  },
  templateSelect: {
    margin: theme.spacing(2, 0),
  },
  status: {
    justifySelf: 'center',
  },
}));

const messages = defineMessages({
  title: {
    id: 'SurveyTemplatePreview.title',
    defaultMessage: 'Template Preview',
  },
  currentTemplate: {
    id: 'SurveyTemplatePreview.currentTemplate',
    defaultMessage: 'Current template',
  },
  previewIsUpToDate: {
    id: 'SurveyTemplatePreview.previewIsUpToDate',
    defaultMessage: 'Preview is up to date',
  },
  previewIsUpdating: {
    id: 'SurveyTemplatePreview.previewIsUpdating',
    defaultMessage: 'Preview is updating...',
  },
  previewIsError: {
    id: 'SurveyTemplatePreview.previewIsError',
    defaultMessage: 'Preview failed to generate, make a change to update.',
  },
});

const Status = ({ type }: { type: 'current' | 'updating' | 'error' }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const icon = {
    current: <CheckCircle color={theme.palette.success.main} />,
    updating: <DotLoader size={0} />,
    error: <XCircle color={theme.palette.error.main} />,
  }[type];

  const message = {
    current: messages.previewIsUpToDate,
    updating: messages.previewIsUpdating,
    error: messages.previewIsError,
  }[type];

  return (
    <Box gap={1}>
      <Box width={2.5} height={2.5}>
        {icon}
      </Box>
      <Text size="subbody">{formatMessage(message)}</Text>
    </Box>
  );
};

export const SurveyTemplatePreview = ({ response, surveyId }: Props) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const [previewSrc, setPreviewSrc] = useState<string>();
  const [responseFhir, setResponseFhir] = useState<QuestionnaireResponse>();
  const [activeTemplate, setActiveTemplate] = useState<SurveyTemplateWithId>();

  const { surveyTemplates } = useSurveyTemplates({ surveyId });
  const testResponse = useTestTemplate({
    template: activeTemplate?.templateConfig.template || '',
    response: responseFhir,
    surveyId,
    disableNotifications: true,
  });

  useCustomCompareEffect(
    () => setResponseFhir(response.toFhirQuestionnaireResponse()),
    [response],
    ([prevResponse], [nextResponse]) => {
      const prevFhir = prevResponse.toFhirQuestionnaireResponse();
      const nextFhir = nextResponse.toFhirQuestionnaireResponse();
      return _isEqual(prevFhir?.item, nextFhir?.item);
    },
  );

  useEffect(
    () => setActiveTemplate(current => current || surveyTemplates[0]),
    [surveyTemplates],
  );

  useEffect(() => {
    if (testResponse.testResponse?.result) {
      setPreviewSrc(testResponse.testResponse.result);
    }
  }, [testResponse.testResponse?.result]);

  const status = testResponse.isFetching
    ? 'updating'
    : testResponse.testResponse?.error
    ? 'error'
    : 'current';

  if (!activeTemplate || !testResponse.isFetched) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Text size="headline" marginBottom>
        {formatMessage(messages.title)}
      </Text>
      {surveyTemplates.length > 1 && (
        <Select
          label={formatMessage(messages.currentTemplate)}
          value={activeTemplate.templateId}
          className={classes.templateSelect}
          onChange={value => {
            const template = surveyTemplates.find(
              template => template.templateId === value,
            );
            if (template) {
              setActiveTemplate(template);
            }
          }}
        >
          {surveyTemplates.map(template => (
            <SelectOption
              key={template.templateId}
              title={template.name}
              value={template.templateId}
            />
          ))}
        </Select>
      )}
      {previewSrc && <TemplateResult templateResult={previewSrc} />}
      <Box className={classes.status}>
        <Status type={status} />
      </Box>
    </div>
  );
};

export const canViewTemplatePreview = (policy: Policy) => {
  const attributes = { resource: { type: 'survey-template' } };
  return (
    enforce(permissions.readData, policy, attributes) &&
    enforce(permissions.createData, policy, attributes)
  );
};
